import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';

// Shadcn UI Components
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Spinner } from '../components/ui/spinner';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select';
import { Alert, AlertTitle, AlertDescription } from '../components/ui/alert';
import { PageHeader } from '../components/common';
import { Download } from 'lucide-react';

const VehicleSearch = () => {
  const navigate = useNavigate();
  const [configs, setConfigs] = useState([]);
  const [selectedConfig, setSelectedConfig] = useState('');
  const [plate, setPlate] = useState('');
  const [loading, setLoading] = useState(false);
  const [downloadingZip, setDownloadingZip] = useState(false);
  const [error, setError] = useState(null);
  const [vehicleData, setVehicleData] = useState(null);

  useEffect(() => {
    // Load vexsoft configs on component mount
    const fetchConfigs = async () => {
      try {
        const response = await api.get('/vexsoft/configs');
        setConfigs(response.data);
      } catch (error) {
        setError('Falha ao carregar configurações');
      }
    };
    fetchConfigs();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setVehicleData(null);

    try {
      const response = await api.post('/vexsoft/search-vehicle', {
        configId: selectedConfig,
        plate: plate.toUpperCase(),
      });

      setVehicleData(response.data);
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to search vehicle');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadZip = async () => {
    if (!vehicleData?.placa) return;

    setDownloadingZip(true);
    try {
      const response = await api.get(`/vexsoft/download-photos/${vehicleData.placa}`, {
        responseType: 'blob', // Importante para receber arquivos binários
      });

      // Criar um URL temporário para o blob
      const url = window.URL.createObjectURL(new Blob([response.data]));
      
      // Criar um link temporário e clicar nele para iniciar o download
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${vehicleData.placa}_fotos.zip`);
      document.body.appendChild(link);
      link.click();
      
      // Limpar
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Erro ao baixar ZIP:', error);
      setError(error.response?.data?.message || 'Erro ao baixar as fotos');
    } finally {
      setDownloadingZip(false);
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader 
        title="Buscar Dados do Veículo"
        description="Pesquise informações de veículos através da placa"
      >
        <Button 
          variant="outline"
          onClick={() => navigate('/admin/veiculos/vexsoft-config')}
        >
          Configurar Locadoras
        </Button>
      </PageHeader>

      <div className="space-y-4">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Select
                value={selectedConfig}
                onValueChange={setSelectedConfig}
                required
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Selecione uma locadora" />
                </SelectTrigger>
                <SelectContent>
                  {configs.map((config) => (
                    <SelectItem key={config._id} value={config._id}>
                      {config.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2">
              <Input
                className="flex-1"
                placeholder="Placa do Veículo"
                value={plate}
                onChange={(e) => setPlate(e.target.value)}
                required
                style={{ textTransform: 'uppercase' }}
              />
              <Button
                type="submit"
                disabled={loading}
                className="min-w-[120px]"
              >
                {loading ? <Spinner className="h-5 w-5" /> : 'Pesquisar'}
              </Button>
            </div>
          </div>
        </form>

        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertTitle>Erro</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {vehicleData && (
          <div className="space-y-4">
            <h2 className="text-2xl font-semibold mb-4">Informações do Veículo</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <p><span className="font-medium">Veículo:</span> {vehicleData.nome_veiculo}</p>
                <p><span className="font-medium">Placa:</span> {vehicleData.placa}</p>
                <p><span className="font-medium">Quilometragem:</span> {new Intl.NumberFormat('pt-BR').format(vehicleData.km_odometro)} km</p>
                <p><span className="font-medium">Nível de Combustível:</span> {vehicleData.nivel_combustivel}%</p>
                <p><span className="font-medium">Tipo de Veículo:</span> {vehicleData.tipo_veiculo}</p>
                <p><span className="font-medium">Data da Vistoria:</span> {new Date(vehicleData.data).toLocaleDateString('pt-BR')}</p>
              </div>
            </div>

            <div className="flex justify-between items-center mt-6 mb-4">
              <h3 className="text-xl font-semibold">Fotos</h3>
              {vehicleData.downloadedPhotos && vehicleData.downloadedPhotos.length > 0 && (
                <Button
                  onClick={handleDownloadZip}
                  disabled={downloadingZip}
                  variant="outline"
                  size="sm"
                  className="flex items-center gap-2"
                >
                  {downloadingZip ? (
                    <Spinner className="h-4 w-4" />
                  ) : (
                    <Download className="h-4 w-4" />
                  )}
                  {downloadingZip ? 'Baixando...' : 'Baixar Todas'}
                </Button>
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              {vehicleData.downloadedPhotos?.map((photo, index) => (
                <Card key={index} className="overflow-hidden">
                  <div className="relative aspect-video bg-muted">
                    <img
                      src={photo.url ? `${api.defaults.baseURL.replace('/api', '')}${photo.url}` : ''}
                      alt={`Vehicle ${index + 1}`}
                      className="object-cover w-full h-full"
                      onError={(e) => {
                        console.error(`Error loading image:`, photo.url);
                        const fullUrl = `${api.defaults.baseURL.replace('/api', '')}${photo.url}`;
                        console.log('Tentando carregar URL:', fullUrl);
                      }}
                    />
                  </div>
                </Card>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VehicleSearch;
